/**
 * Photo → character analysis (Character Editor, left panel — v1).
 *
 * A vision model looks at an uploaded face photo and returns a JSON object that
 * maps onto the Character Editor's OWN vocabulary (the 9 skin tones, the eye &
 * hair colours, the real hair-front/back style folders, freckles/glasses/gender)
 * — NOT a generic palette. So the result can later pre-fill the editor directly.
 *
 * Recognition model (decided with Tamara):
 *   • the photo is read VISUALLY by the model;
 *   • asset CHOICE is TEXT — we hand the model the allowed values below and it
 *     picks one per property;
 *   • hair is the weak link, so v1 FORCES a controlled hair-descriptor vocabulary
 *     ({@link HAIR_DESCRIPTOR}) and asks for the descriptor + a free-text sentence
 *     alongside the best-guess style names. This is the calibration artifact: run
 *     real photos, see whether the models fill the vocabulary cleanly, refine the
 *     wording — THEN (v2) describe every hair asset with the same vocabulary so
 *     matching becomes descriptor-to-descriptor instead of name-guessing.
 *
 * Keys never reach the browser: the request is proxied by the dev-server plugin
 * `analyzeCharacterPlugin` in vite.config.ts (POST /__analyze-character).
 */
import { CHARACTER_SKIN_PALETTE } from './characterPalette';
import { EYE_COLOURS, HAIR_COLOURS, HAIR_TAGS_URL } from './contract';
import { CHARACTER_ASSETS, type CharacterAssets } from './editorAssets';

/** Per-hairstyle tags (asset metadata), keyed by style name, split front/back. */
export interface HairTags {
  front: Record<string, string[]>;
  back: Record<string, string[]>;
}
export const EMPTY_HAIR_TAGS: HairTags = { front: {}, back: {} };

// Tags change only a handful of times per session, so cache them in memory and
// re-fetch only on demand. The editor calls primeHairTags() whenever it saves,
// so the cache never goes stale without a network round-trip.
let hairTagsCache: HairTags | null = null;

/** Update the in-memory tag cache directly (call after saving edited tags). */
export function primeHairTags(tags: HairTags): void {
  hairTagsCache = tags;
}

/**
 * Per-hairstyle tags (hair-tags.json). Cached after the first read; pass
 * `force` to bypass the cache (e.g. the editor's initial load). A failed/absent
 * fetch returns empty tags WITHOUT caching, so it retries next time.
 */
export async function getHairTags(force = false): Promise<HairTags> {
  if (hairTagsCache && !force) return hairTagsCache;
  try {
    const r = await fetch(HAIR_TAGS_URL, { cache: 'no-store' });
    if (r.ok) {
      const j = (await r.json()) as Partial<HairTags>;
      hairTagsCache = { front: j?.front ?? {}, back: j?.back ?? {} };
      return hairTagsCache;
    }
  } catch {
    /* ignore — return empty, don't cache */
  }
  return EMPTY_HAIR_TAGS;
}

// ---- Real hair-style catalogues (the same folders the editor lists) ----
// Assets are per character (`assets/<Character>/hair-front/…`), but this is the
// VOCABULARY handed to the model — the union across characters, so a style only
// Dad owns is still offerable. Mapping the answer back onto a character filters
// it against that character's own folder (`applyAnalysisToSelection`).
const unionStyles = (pick: (a: CharacterAssets) => string[]): string[] =>
  [...new Set(Object.values(CHARACTER_ASSETS).flatMap(pick))].sort();

export const HAIR_FRONT_STYLES = unionStyles((a) => a.hairFront.styles);
export const HAIR_BACK_STYLES = unionStyles((a) => a.hairBack.styles);

// ---- Providers & models (from the reference tester) ----
export type Provider = 'google' | 'openrouter';

export const MODELS: Record<Provider, string[]> = {
  // NOTE: this Google account only exposes the "-latest" aliases; pinned
  // versions (gemini-2.5-flash, 2.0-flash, 2.5-pro, …) 404 with "no longer
  // available to new users" (verified 2026-07-24).
  google: [
    'gemini-flash-latest',
    'gemini-pro-latest',
  ],
  openrouter: [
    'openai/gpt-4o',
    'openai/gpt-4o-mini',
    'anthropic/claude-3.7-sonnet',
    'anthropic/claude-3.5-sonnet',
    'meta-llama/llama-3.2-90b-vision-instruct',
    'qwen/qwen2.5-vl-72b-instruct',
    'google/gemini-2.5-flash',
  ],
};

export const PROVIDER_LABELS: Record<Provider, string> = {
  google: 'Google Gemini (direct)',
  openrouter: 'OpenRouter',
};

/**
 * Forced hair-descriptor vocabulary. FRONT axes describe how hair frames the
 * face (fringe / parting); BACK axes describe the mass (length / texture /
 * arrangement). Grounded in the real hair-front & hair-back folder names.
 */
export const HAIR_DESCRIPTOR = {
  texture: ['straight', 'wavy', 'curly', 'coily-afro', 'dreadlocked'],
  front: {
    fringeType: ['none', 'straight', 'curtain', 'side-swept-left', 'side-swept-right', 'messy'],
    fringeLength: ['none', 'short', 'long'],
    parting: ['none', 'middle', 'side', 'pulled-back'],
  },
  back: {
    length: ['buzzed', 'short', 'neck', 'shoulder', 'long'],
    volume: ['flat', 'medium', 'voluminous'],
    arrangement: [
      'loose', 'bob', 'bowl', 'ponytail-high', 'ponytail-low',
      'pigtails-low', 'pigtails-high', 'pigtails-high-shoulder', 'braid', 'puff', 'afro', 'dreads', 'buzzed',
    ],
  },
} as const;

/** Informational eye-shape values (eyeShape is not bound in the .riv yet). */
export const EYE_SHAPES = ['round', 'almond', 'narrow', 'wide'] as const;

/**
 * PREFERRED (not forced) hairstyle pairings, authored by Tamara. Keyed BACK →
 * fronts: choose the back (the main hair mass) first, then a front that pairs
 * with it. The model leans toward these when they fit, but the photo always
 * wins. Names are validated against the real folders when the prompt is built
 * (see `preferredCombosText`), so a stale name is silently dropped rather than
 * telling the model to prefer a style that doesn't exist.
 *
 * Name mappings confirmed by Tamara 2026-07-27: "Curly Short"→"Curly Neck" and
 * "Pigtails High Long"→"Pigtails High Shoulder" (renamed folders).
 */
export const PREFERRED_COMBOS: { back: string; fronts: string[] }[] = [
  { back: 'Bowl Cut', fronts: ['Bowl Cut'] },
  { back: 'XAfro', fronts: ['XAfro Short', 'Rows'] },
  { back: 'Dreads Long', fronts: ['Dreads Long', 'Rows'] },
  { back: 'Dreads Short', fronts: ['Dreads Short', 'Rows'] },
  // Curly ↔ curly is handled by a DYNAMIC rule in preferredFrontsFor (every
  // "curly"-named back prefers every "curly"-named front), so no static entries.
  { back: 'Buzzed', fronts: ['Short'] },
  { back: 'Pigtails High Shoulder', fronts: ['Pulled Back', 'Pulled Back Middle Part'] },
  { back: 'Pigtails High', fronts: ['Pulled Back', 'Pulled Back Middle Part'] },
  { back: 'Ponytail High', fronts: ['Pulled Back', 'Pulled Back Middle Part'] },
  { back: 'Puff Two', fronts: ['Pulled Back', 'Pulled Back Middle Part'] },
  { back: 'Ponytail Low', fronts: ['Part', 'Fringe Curtain'] },
];

/** Preferred fronts for a back (static combos + the dynamic curly↔curly rule),
 *  filtered to real folders (case-insensitive). */
export function preferredFrontsFor(back: string): string[] {
  const b = String(back ?? '').toLowerCase();
  const out = new Set<string>();
  const c = PREFERRED_COMBOS.find((x) => x.back.toLowerCase() === b);
  if (c) c.fronts.forEach((f) => out.add(f));
  // Dynamic: every "curly"-named back prefers every "curly"-named front
  // (Tamara: "all that have curly in the name"). Auto-adapts to new curly folders.
  if (b.includes('curly')) {
    HAIR_FRONT_STYLES.filter((s) => s.toLowerCase().includes('curly')).forEach((f) => out.add(f));
  }
  return [...out].filter((f) => f === NO_FRONT || HAIR_FRONT_STYLES.some((s) => s.toLowerCase() === f.toLowerCase()));
}

/**
 * A front of "none" is tolerated from the model but never wanted: in this rig
 * the buzz cut IS a front layer ("Short", the close-cropped top) and the back
 * "Buzzed" is only the rim behind the head, so an empty front slot renders a
 * bald child (Lenart, 2026-09-17). The apply side maps it to {@link NO_FRONT_FALLBACK}.
 */
export const NO_FRONT = 'none';
export const NO_FRONT_FALLBACK = 'Short';

/** The result shape the model is asked to return (also the editor-apply target). */
export interface AnalysisResult {
  gender: 'girl' | 'boy';
  skinTone: number; // 1…N (index into CHARACTER_SKIN_PALETTE, 1-based)
  eyeColour: string; // one of EYE_COLOURS names
  eyeShape: string; // one of EYE_SHAPES (informational)
  freckles: boolean;
  glasses: boolean;
  hairColour: string; // one of HAIR_COLOURS names
  hairFrontStyle: string; // one of HAIR_FRONT_STYLES
  hairBackStyle: string; // one of HAIR_BACK_STYLES
  hairDescription: string; // one short free-text sentence (reasoning / debug aid)
}

const list = (arr: readonly string[]) => arr.map((s) => `"${s}"`).join(', ');

/** Human-readable schema (allowed values) injected into the system prompt. */
/** Rough word hints per skin tone (indexed to CHARACTER_SKIN_PALETTE, which is
 *  ordered darkest→lightest). LLMs match skin far better against words than raw
 *  hex, and this keeps the model from over-darkening. */
const SKIN_TONE_HINTS = [
  'very dark brown', 'dark brown', 'deep brown', 'medium-dark brown',
  'medium brown / tan', 'tan', 'light tan', 'fair', 'very fair',
];

/** One line per style, appending "— tags: …" when the style has any. */
function styleLines(styles: string[], tagMap?: Record<string, string[]>): string[] {
  return styles.map((s) => {
    const t = tagMap?.[s];
    return t && t.length ? `    ${s} — tags: ${t.join(', ')}` : `    ${s}`;
  });
}

export function buildSchemaText(tags: HairTags = EMPTY_HAIR_TAGS): string {
  const n = CHARACTER_SKIN_PALETTE.length;
  const skinLines = CHARACTER_SKIN_PALETTE.map(
    (t, i) => `    ${i + 1} = ${t.baseHex}${SKIN_TONE_HINTS[i] ? ` (${SKIN_TONE_HINTS[i]})` : ''}`,
  ).join('\n');
  return [
    `- gender: "girl" | "boy"`,
    `- skinTone: 1..${n}, closest to the person's real skin (1 = darkest, ${n} = lightest). Judge well-lit areas, ignore shadows, don't over-darken — tones 1-2 are for very dark skin only:`,
    skinLines,
    `- eyeColour: one of ${list(EYE_COLOURS.map((c) => c.name))}`,
    `- eyeShape: one of ${list(EYE_SHAPES)}`,
    `- freckles: true | false`,
    `- glasses: true | false`,
    `- hairColour: one of ${list(HAIR_COLOURS.map((c) => c.name))} — pick the closest; dark brown or near-black hair → "Black" (reserve "Brown" for clearly medium/warm brown).`,
    `- hairBackStyle: choose this FIRST (the main hair mass) — closest BACK style below, matching the photo to the tags:`,
    ...styleLines(HAIR_BACK_STYLES, tags.back),
    `- hairFrontStyle: choose AFTER the back — closest FRONT style below (see preferred pairings), matching the photo to the tags. For boys (gender "boy") prefer "Fringe Messy" or "Fringe Messy Long" over the straight fringes ("Fringe Straight Short" / "Fringe Straight Long"):`,
    ...styleLines(HAIR_FRONT_STYLES, tags.front),
    ...preferredCombosText(),
    `- hairDescription: ONE short sentence describing the hair`,
  ].join('\n');
}

/** Preferred back→front pairing lines for the prompt (only combos whose back
 *  and ≥1 front are real folders). Empty when none apply. */
function preferredCombosText(): string[] {
  // Backs with a preference = static combos + every "curly"-named back.
  const backs = new Set<string>(PREFERRED_COMBOS.map((c) => c.back));
  HAIR_BACK_STYLES.filter((s) => s.toLowerCase().includes('curly')).forEach((b) => backs.add(b));
  const lines = [...backs]
    .filter((b) => HAIR_BACK_STYLES.some((s) => s.toLowerCase() === b.toLowerCase()))
    .map((b) => {
      const fronts = preferredFrontsFor(b);
      return fronts.length ? `    ${b} → ${fronts.join(' / ')}` : null;
    })
    .filter((l): l is string => l !== null);
  if (!lines.length) return [];
  return [
    `- PREFERRED back→front pairings — after choosing the back, if it is listed here pick one of its paired fronts UNLESS the photo clearly shows otherwise (a preference, not a rule):`,
    ...lines,
  ];
}

/** A filled-in example (guides the model's exact JSON shape). */
export function buildExample(): string {
  const ex: AnalysisResult = {
    gender: 'girl',
    skinTone: 3,
    eyeColour: EYE_COLOURS[1]?.name ?? 'Blue',
    eyeShape: 'almond',
    freckles: true,
    glasses: false,
    hairColour: HAIR_COLOURS[1]?.name ?? 'Blonde',
    hairFrontStyle: HAIR_FRONT_STYLES[0] ?? 'Fringe Curtain',
    hairBackStyle: HAIR_BACK_STYLES[0] ?? 'Straight Long',
    hairDescription: 'Shoulder-length wavy blonde hair with a middle-parted curtain fringe.',
  };
  return JSON.stringify(ex, null, 2);
}

export const DEFAULT_SYSTEM_PROMPT = `You turn a photo of a person into a stylised cartoon character built from a fixed set of tagged assets.
Return ONLY a JSON object — exactly one allowed value per property, never a value outside the lists, no markdown or explanation.

WHO TO DESCRIBE: the photo is of a CHILD. If more than one person is visible, describe the child, never an adult; if several children are visible, the one with the largest or most central face. Base every property on that one person only.

For hair, choose the BACK first (the main hair mass), then a FRONT that pairs with it — match the photo to the style whose tags fit best. When styles differ only by length/position, prefer the more specific one (e.g. pick a "Shoulder" or "Long" variant for longer hair).
FRONT RULES (these override the general preference below):
- Hair gathered away from the face — a ponytail, pigtails, a bun, puffs, a headband or clips holding the front hair back — has NO fringe: the front is "Pulled Back" (or "Pulled Back Middle Part" when a parting shows), unless a cut fringe clearly hangs over the forehead.
- "Fringe Straight Short" / "Fringe Straight Long" mean a straight-cut fringe that covers the forehead. Hair parted in the middle and falling to the sides is NOT a straight fringe: use "Fringe Curtain", "Part" or "Pulled Back Middle Part".
- A buzz cut or very short hair (back "Buzzed") has no fringe: the front is "Short" (the close-cropped top, no fringe).
- "Buzzed" is ONLY clipper-short hair with the scalp showing through. Short coily or afro-textured hair that still has volume is NOT buzzed: use back "XAfro" with front "XAfro Short" (or "Rows" for cornrows).

Properties and allowed values:
{SCHEMA}

Return only valid JSON, e.g.:
{EXAMPLE}`;

export const DEFAULT_USER_PROMPT =
  'Analyse the person in this photo and describe them as a cartoon character using the given properties. Be precise about the hair.';

/**
 * Prose hair description — the authoring counterpart to the JSON preset. Asks
 * for free text instead of a picked asset, along the SAME axes as
 * {@link HAIR_DESCRIPTOR}, so the answer can be pasted straight into the
 * per-hairstyle tags. Deliberately contains NO {SCHEMA}/{EXAMPLE} placeholder:
 * leaving those in re-injects the whole allowed-values list plus a worked JSON
 * example, which overrides any "write prose" instruction above it.
 */
export const DESCRIBE_HAIR_SYSTEM_PROMPT = `You describe the hair of the person in a photo, for a cartoon-asset artist.
Write plain prose — no JSON, no markdown, no bullet lists, no property names.

Cover, in this order, in 3–5 sentences:
1. BACK (the main hair mass): length (buzzed / short / to the neck / to the shoulders / long), volume (flat / medium / voluminous), and how it is arranged (loose, bob, bowl, high or low ponytail, high or low pigtails, braid, puff, afro, dreads).
2. FRONT (how the hair frames the face): fringe type (none / straight / curtain / side-swept left / side-swept right / messy), fringe length (short / long), and parting (none / middle / side / pulled back).
3. TEXTURE: straight, wavy, curly, coily-afro or dreadlocked.
4. COLOUR: the everyday name for it, plus whether it is warm or cool and light or dark.

Describe only what you can actually see. If the back of the head is hidden, say what the hair most likely does back there and say you are inferring it. Do not mention skin, eyes, clothes or anything but the hair.`;

export const DESCRIBE_HAIR_USER_PROMPT = "Describe this person's hair.";

/** A prompt preset the panel can load into the Advanced editor. */
export interface PromptPreset {
  id: 'character' | 'hair';
  label: string;
  system: string;
  user: string;
  jsonMode: boolean;
  /** False for prose presets — the panel then skips JSON parsing/validation. */
  expectsJson: boolean;
}

export const PROMPT_PRESETS: PromptPreset[] = [
  {
    id: 'character',
    label: 'Character JSON',
    system: DEFAULT_SYSTEM_PROMPT,
    user: DEFAULT_USER_PROMPT,
    jsonMode: true,
    expectsJson: true,
  },
  {
    id: 'hair',
    label: 'Describe hair',
    system: DESCRIBE_HAIR_SYSTEM_PROMPT,
    user: DESCRIBE_HAIR_USER_PROMPT,
    jsonMode: false,
    expectsJson: false,
  },
];

export type PresetId = PromptPreset['id'];

export const getPreset = (id: PresetId): PromptPreset =>
  PROMPT_PRESETS.find((p) => p.id === id) ?? PROMPT_PRESETS[0];

/** Fill {SCHEMA}/{EXAMPLE} placeholders in a system-prompt template. */
export function fillPrompt(template: string, tags: HairTags = EMPTY_HAIR_TAGS): string {
  return template
    .replace('{SCHEMA}', buildSchemaText(tags))
    .replace('{EXAMPLE}', buildExample());
}

// ---- Client (talks to the dev-server proxy) ----
export interface AnalyzeResponse {
  text: string;
  model: string;
  usage: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | null;
  elapsedMs: number;
}

export async function analyzePhoto(req: {
  provider: Provider;
  model: string;
  imageDataUrl: string;
  systemPrompt: string;
  userPrompt: string;
  jsonMode: boolean;
}): Promise<AnalyzeResponse> {
  // Fold the hand-authored per-hairstyle tags into the prompt so the model
  // matches the photo against tagged styles, not just names.
  const tags = await getHairTags();
  const r = await fetch('/__analyze-character', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...req, systemPrompt: fillPrompt(req.systemPrompt, tags) }),
  });
  const data = await r.json();
  if (!r.ok) {
    const err = new Error(data?.error || `Analyse failed (${r.status})`) as Error & { status?: number };
    err.status = data?.status ?? r.status;
    throw err;
  }
  return data as AnalyzeResponse;
}

/** Which provider keys are configured on the dev server (for upfront warnings). */
export interface ProviderStatus {
  google: { configured: boolean };
  openrouter: { configured: boolean };
}

export async function getProviderStatus(): Promise<ProviderStatus | null> {
  try {
    const r = await fetch('/__character-status');
    return r.ok ? ((await r.json()) as ProviderStatus) : null;
  } catch {
    return null;
  }
}

/**
 * Flag any returned value that is NOT in the editor's allowed vocabulary (a
 * missing field, an out-of-range skin tone, an unknown colour / style / hair
 * descriptor). This is the key calibration signal — e.g. a model inventing a
 * hairstyle name that we don't have an asset for.
 */
export function validateResult(p: AnalysisResult | null): string[] {
  if (!p) return ['Could not parse a JSON object from the response.'];
  const warn: string[] = [];
  const inSet = (v: string, set: readonly string[]) => set.some((s) => s.toLowerCase() === String(v ?? '').toLowerCase());

  if (p.gender !== 'girl' && p.gender !== 'boy') warn.push(`gender: "${p.gender}" (expected "girl" or "boy")`);
  if (typeof p.skinTone !== 'number' || p.skinTone < 1 || p.skinTone > CHARACTER_SKIN_PALETTE.length)
    warn.push(`skinTone: ${p.skinTone} (expected 1…${CHARACTER_SKIN_PALETTE.length})`);
  if (!inSet(p.eyeColour, EYE_COLOURS.map((c) => c.name))) warn.push(`eyeColour: "${p.eyeColour}" not in the allowed eye colours`);
  if (!inSet(p.eyeShape, EYE_SHAPES)) warn.push(`eyeShape: "${p.eyeShape}" not in ${EYE_SHAPES.join('/')}`);
  if (typeof p.freckles !== 'boolean') warn.push(`freckles: "${p.freckles}" (expected true/false)`);
  if (typeof p.glasses !== 'boolean') warn.push(`glasses: "${p.glasses}" (expected true/false)`);
  if (!inSet(p.hairColour, HAIR_COLOURS.map((c) => c.name))) warn.push(`hairColour: "${p.hairColour}" not in the allowed hair colours`);
  if (String(p.hairFrontStyle).toLowerCase() !== NO_FRONT && !inSet(p.hairFrontStyle, HAIR_FRONT_STYLES)) warn.push(`hairFrontStyle: "${p.hairFrontStyle}" — no matching front asset`);
  if (!inSet(p.hairBackStyle, HAIR_BACK_STYLES)) warn.push(`hairBackStyle: "${p.hairBackStyle}" — no matching back asset`);
  return warn;
}

/** Strip ```json fences / prose and parse the first JSON object in the text. */
export function tryParseResult(text: string): AnalysisResult | null {
  if (!text) return null;
  let t = text.trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
  const start = t.indexOf('{');
  const end = t.lastIndexOf('}');
  if (start !== -1 && end !== -1 && end > start) t = t.slice(start, end + 1);
  try {
    return JSON.parse(t) as AnalysisResult;
  } catch {
    return null;
  }
}

// ---- History ----
export interface HistoryRecord {
  id: string;
  ts: number;
  provider: string;
  model: string;
  jsonMode: boolean;
  systemPrompt: string;
  userPrompt: string;
  output: string;
  parsed: AnalysisResult | null;
  corrected: AnalysisResult | null;
  usage: AnalyzeResponse['usage'];
  elapsedMs: number | null;
  note: string;
  /** A small (~120px) JPEG thumbnail of the uploaded photo, for identification
   *  in the history list. The full-resolution photo is deliberately NOT stored. */
  thumb: string | null;
}

export async function listHistory(): Promise<HistoryRecord[]> {
  const r = await fetch('/__character-history');
  return r.ok ? ((await r.json()) as HistoryRecord[]) : [];
}

export async function saveHistory(
  rec: Omit<HistoryRecord, 'id' | 'ts' | 'note'>,
): Promise<{ ok: boolean; id?: string }> {
  const r = await fetch('/__character-history', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rec),
  });
  return r.json();
}

export async function clearHistory(): Promise<void> {
  await fetch('/__character-history', { method: 'DELETE' });
}

export async function deleteHistoryItem(id: string): Promise<void> {
  await fetch(`/__character-history?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
}
